import { PlusOutlined } from '@ant-design/icons';
import { history, useModel, useRequest } from '@umijs/max';
import { Button, Input, Modal, Select, Space, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createWorkspace,
  listWorkspaces,
  WORKSPACE_STORAGE_KEY,
} from '@/services/workspace';

type WorkspaceItem = {
  id: number;
  name: string;
  slug: string;
  type: string;
  role?: string;
};

const WorkspaceSwitcher: React.FC = () => {
  const { initialState, setInitialState } = useModel('@@initialState');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  // useRequest 默认已取响应里的 data 字段，这里拿到的就是工作区数组
  const { data, loading, refresh } = useRequest(() => listWorkspaces());

  const workspaces: WorkspaceItem[] = data || [];
  const storedId = Number(localStorage.getItem(WORKSPACE_STORAGE_KEY) || 0);
  const currentId = useMemo(() => {
    if (workspaces.some((w) => w.id === storedId)) return storedId;
    const personal = workspaces.find((w) => w.type === 'personal');
    return personal?.id || workspaces[0]?.id || undefined;
  }, [workspaces, storedId]);

  useEffect(() => {
    if (!currentId) return;
    if (String(storedId) !== String(currentId)) {
      localStorage.setItem(WORKSPACE_STORAGE_KEY, String(currentId));
    }
    const current = workspaces.find((w) => w.id === currentId);
    setInitialState((s) => ({
      ...s,
      currentWorkspace: current || null,
    }));
  }, [currentId, workspaces, storedId, setInitialState]);

  const onChange = useCallback(
    (id: number) => {
      localStorage.setItem(WORKSPACE_STORAGE_KEY, String(id));
      const current = workspaces.find((w) => w.id === id);
      setInitialState((s) => ({
        ...s,
        currentWorkspace: current || null,
      }));
      // 刷新当前页数据
      history.go(0);
    },
    [workspaces, setInitialState],
  );

  const onCreate = async () => {
    const name = newName.trim();
    if (!name) {
      message.warning('请输入工作区名称');
      return;
    }
    setCreating(true);
    try {
      const res = await createWorkspace({ name });
      const ws = res?.data;
      if (ws?.id) {
        localStorage.setItem(WORKSPACE_STORAGE_KEY, String(ws.id));
        message.success('已创建工作区');
        setCreateOpen(false);
        setNewName('');
        await refresh();
        history.go(0);
      }
    } catch (e: any) {
      message.error(e?.message || '创建失败');
    } finally {
      setCreating(false);
    }
  };

  if (!initialState?.currentUser) return null;

  return (
    <>
      <Space size={4} style={{ marginRight: 12 }}>
        <Select
          size="small"
          loading={loading}
          style={{ minWidth: 140 }}
          placeholder="工作区"
          value={currentId}
          options={workspaces.map((w) => ({
            value: w.id,
            label: w.type === 'personal' ? `${w.name}（个人）` : w.name,
          }))}
          onChange={onChange}
        />
        <Button
          size="small"
          type="text"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
          title="新建协作工作区"
        />
      </Space>
      <Modal
        title="新建协作工作区"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={onCreate}
        confirmLoading={creating}
        destroyOnClose
      >
        <Input
          placeholder="例如：投递协作"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={onCreate}
        />
      </Modal>
    </>
  );
};

export default WorkspaceSwitcher;
